import React, { useMemo } from 'react';
import {
    PieChart, Pie, Cell,
    BarChart, Bar,
    LineChart, Line,
    AreaChart, Area,
    RadialBarChart, RadialBar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { detectChartKeys, type ChartSeriesInput } from '../../../../../utils/chartUtils';
import { VizSwitcher, type VizType } from './VizSwitcher';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ChartSeries {
    name: string;
    data: Record<string, any>[];
    color?: string;
    dataKey?: string;
}

interface ChartAxisConfig {
    label?: string;
    dataKey: string;
    format?: string;
}

interface ChartData {
    chartType: string;
    title: string;
    series: ChartSeries[];
    xAxis?: ChartAxisConfig;
    yAxis?: ChartAxisConfig;
}

interface ChartBlockProps {
    data: ChartData;
    /** Callback for viz type switching */
    onVizSwitch?: (type: VizType) => void;
    /** Currently active viz type (for VizSwitcher highlight) */
    currentVizType?: VizType;
    /** Callback for drill-down when user clicks a data point */
    onDataPointClick?: (label: string, value: number) => void;
}

const COLORS = [
    '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b',
    '#ef4444', '#06b6d4', '#ec4899', '#6366f1',
];

export const ChartBlock: React.FC<ChartBlockProps> = ({ data, onVizSwitch, currentVizType, onDataPointClick }) => {
    const { t } = useTranslation();
    const activeType = currentVizType || (data.chartType?.toLowerCase() as VizType) || 'bar';
    const height = 300;

    const { xKey, yKeys, flatData, isMultiSeries: multi } = useMemo(
        () => detectChartKeys(
            data.series?.[0]?.data || [],
            data.series as ChartSeriesInput[],
            data.xAxis,
            data.yAxis
        ),
        [data.series, data.xAxis, data.yAxis]
    );

    if (!flatData.length) {
        return <p className="text-sm text-gray-500 py-4">{t('manager.modules.claudeQuery.chart.noData', {}, 'Ingen data for diagrammet')}</p>;
    }

    /** Find the color for a given yKey from series config */
    const getColor = (key: string, idx: number): string => {
        const match = data.series.find(s => (s.dataKey || s.name) === key);
        return match?.color || COLORS[idx % COLORS.length];
    };

    /** Find the display name for a given yKey from series config */
    const getName = (key: string): string => {
        const match = data.series.find(s => (s.dataKey || s.name) === key);
        return match?.name || key;
    };

    const renderChart = () => {
        if (activeType === 'pie') {
            const pieData = data.series?.[0]?.data || flatData;
            const pieYKey = yKeys[0];
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey={pieYKey}
                            nameKey={xKey}
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label
                        >
                            {pieData.map((_entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'bar') {
            const handleBarClick = onDataPointClick
                ? (entry: any) => {
                    if (entry && entry.activePayload?.[0]) {
                        const payload = entry.activePayload[0].payload;
                        const label = String(payload[xKey] || '');
                        const value = payload[yKeys[0]] ?? 0;
                        onDataPointClick(label, value);
                    }
                }
                : undefined;

            return (
                <ResponsiveContainer width="100%" height={height}>
                    <BarChart
                        data={flatData}
                        onClick={handleBarClick}
                        style={onDataPointClick ? { cursor: 'pointer' } : undefined}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        {yKeys.map((key, idx) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                fill={getColor(key, idx)}
                                name={getName(key)}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'stacked-bar') {
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <BarChart data={flatData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        {yKeys.map((key, idx) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                fill={getColor(key, idx)}
                                name={getName(key)}
                                stackId="stack"
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'horizontal-bar') {
            return (
                <ResponsiveContainer width="100%" height={Math.max(height, flatData.length * 35)}>
                    <BarChart data={flatData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis type="category" dataKey={xKey} tick={{ fontSize: 11 }} width={120} />
                        <Tooltip />
                        <Legend />
                        {yKeys.map((key, idx) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                fill={getColor(key, idx)}
                                name={getName(key)}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'gauge') {
            // Gauge works best with a single value (0-100 range)
            const gaugeValue = typeof flatData[0][yKeys[0]] === 'number'
                ? flatData[0][yKeys[0]]
                : parseFloat(flatData[0][yKeys[0]]) || 0;
            const clampedValue = Math.min(100, Math.max(0, gaugeValue));

            const gaugeData = [
                { name: getName(yKeys[0]), value: clampedValue, fill: COLORS[0] },
            ];

            return (
                <div className="flex flex-col items-center">
                    <ResponsiveContainer width="100%" height={220}>
                        <RadialBarChart
                            cx="50%"
                            cy="50%"
                            innerRadius="60%"
                            outerRadius="90%"
                            data={gaugeData}
                            startAngle={180}
                            endAngle={0}
                            barSize={20}
                        >
                            <RadialBar
                                dataKey="value"
                                cornerRadius={10}
                                background={{ fill: '#f3f4f6' }}
                            />
                            <Tooltip />
                        </RadialBarChart>
                    </ResponsiveContainer>
                    <div className="text-center -mt-16 mb-4">
                        <p className="text-3xl font-bold text-gray-900">{gaugeValue}%</p>
                        <p className="text-xs text-gray-500">{getName(yKeys[0])}</p>
                    </div>
                </div>
            );
        }

        if (activeType === 'line') {
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <LineChart data={flatData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        {yKeys.map((key, idx) => (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={getColor(key, idx)}
                                name={getName(key)}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'area') {
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <AreaChart data={flatData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        {yKeys.map((key, idx) => (
                            <Area
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={getColor(key, idx)}
                                fill={getColor(key, idx)}
                                fillOpacity={0.6}
                                name={getName(key)}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            );
        }

        return <p className="text-sm text-gray-500">{t('manager.modules.claudeQuery.chart.unknownType', { '0': activeType }, `Ukjent graf-type: ${activeType}`)}</p>;
    };

    return (
        <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
                {data.title ? (
                    <h4 className="text-sm font-semibold text-gray-700">{data.title}</h4>
                ) : (
                    <div />
                )}
                {onVizSwitch && (
                    <VizSwitcher
                        currentType={activeType}
                        onSwitch={onVizSwitch}
                        sourceBlockType="chart"
                    />
                )}
            </div>
            {renderChart()}
        </div>
    );
};
